import { usePathname } from 'next/navigation'
import { useCallback, useEffect } from 'react'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../../../../brunswick/containers/cart'
import { useEmbroideryPrices } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryPricingProvider'
import { useEmbroiderySelection } from '../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import {
  firstLogoItem,
  missingVariant,
} from '../../../../../containers/EmbroideryContainer/utils/buildV2EmbroideryLineSet'
import { reportEmbroideryError } from '../../../../../containers/EmbroideryContainer/utils/reportEmbroideryError'
import { hasLogoSetupLineForLogo } from '../../../../../containers/EmbroideryContainer/utils/unorganized'
import { ModalFooter } from '../../parts/ModalFooter/ModalFooter'
import { PriceSummary } from '../../parts/PriceSummary/PriceSummary'

import { useDtcEmbroideryAddToBag } from './useDtcEmbroideryAddToBag'

/** Clears every choice, offered once there is something to clear. */
const useResetButton = () => {
  const {
    embroidery: {
      modal: {
        forms: { submissionButtons },
      },
    },
  } = useTranslation()
  const { itemsEnabledForBag, clear } = useEmbroiderySelection()

  return {
    secondaryLabel: itemsEnabledForBag.length > 0 ? submissionButtons.resetCta : undefined,
    onSecondary: clear,
  }
}

/**
 * Whether the bag can buy everything the shopper has chosen. A choice the catalog has no product
 * for can't be added, so the footers hold it back rather than let the add fail.
 */
const useCanBuyChosenItems = () => {
  const { cart } = CartContainer.useContainer()
  const { itemsEnabledForBag } = useEmbroiderySelection()
  const { v2EmbroideryVariants, variantsStatus } = useEmbroideryPrices()
  const logoSetupInCart = hasLogoSetupLineForLogo(
    cart.items,
    firstLogoItem(itemsEnabledForBag)?.values.logoFile?.id
  )
  const missing = missingVariant(itemsEnabledForBag, v2EmbroideryVariants, logoSetupInCart)
  const pathname = usePathname()

  // Not while variants load, so a pending query isn't reported as a missing product. A failed
  // query reports too, since it leaves every type missing. Deduped per page so each product
  // still reports after client-side navigation.
  useEffect(() => {
    if (missing === undefined || variantsStatus === 'pending') return
    reportEmbroideryError(
      'embroidery-add-to-bag-unavailable',
      { missing, variantsStatus },
      { dedupeKey: `${missing}:${pathname}` }
    )
  }, [missing, variantsStatus, pathname])

  return missing === undefined
}

export type ConfirmFooterProps = {
  onConfirm(): void
}

/**
 * Where there is no variant to buy yet (a kit's top, before the pants are chosen), the footer only
 * confirms the embroidery: no price summary, and nothing to confirm until something is chosen.
 */
export const ConfirmFooter: React.FC<ConfirmFooterProps> = ({ onConfirm }) => {
  const {
    embroidery: {
      modal: { confirm },
    },
  } = useTranslation()
  const { itemsEnabledForBag } = useEmbroiderySelection()
  const resetButton = useResetButton()
  const canBuy = useCanBuyChosenItems()
  const disabled = itemsEnabledForBag.length === 0 || !canBuy

  return (
    <ModalFooter
      primaryLabel={confirm}
      onPrimary={disabled ? () => undefined : onConfirm}
      primaryDisabled={disabled}
      {...resetButton}
    />
  )
}

export type AddToBagFooterProps = {
  /** Confirms the embroidery just before the form adds it to the bag with the garment. */
  onAdded(): void
}

/**
 * Inside the variant selection form the footer puts the garment and its embroidery in the bag. Must
 * be rendered inside the variant selection form.
 */
export const AddToBagFooter: React.FC<AddToBagFooterProps> = ({ onAdded }) => {
  const {
    embroidery: { addToBag: addToBagLabel, selectASize },
  } = useTranslation()
  const { addToBag, isDisabled, productPriceInCents } = useDtcEmbroideryAddToBag()
  const resetButton = useResetButton()
  const canBuy = useCanBuyChosenItems()
  const disabled = isDisabled || !canBuy

  const handleAddToBag = useCallback(() => addToBag(onAdded), [addToBag, onAdded])

  return (
    <ModalFooter
      primaryLabel={isDisabled ? selectASize : addToBagLabel}
      onPrimary={disabled ? () => undefined : handleAddToBag}
      primaryDisabled={disabled}
      {...resetButton}
    >
      <PriceSummary productPriceInCents={productPriceInCents} />
    </ModalFooter>
  )
}
